<template>
  <div class="permisos-contenido">
    <LoadingOverlay :visible="cargando" message="Cargando permisos..." />
    <MensajeToast ref="toast" />

    <div class="permisos-header">
      <div class="permisos-titulo">
        <h2>Permisos del rol</h2>
        <p class="permisos-sub" v-if="props.rolLabel">
          Rol: <strong>{{ props.rolLabel }}</strong>
        </p>
      </div>
      <div class="permisos-acciones">
        <button class="btn" @click="borrarTodos" :disabled="guardando">
          Borrar permisos
        </button>
        <button class="btn-cerrar" @click="emit('cerrar')" aria-label="Cerrar">✕</button>
      </div>
    </div>

    <!-- Buscador -->
    <div class="buscador">
      <input type="text" v-model="busqueda" placeholder="Buscar permisos" />
    </div>

    <!-- Categorías -->
    <div v-if="categoriasFiltradas.length">
      <div
        v-for="(cat, ci) in categoriasFiltradas"
        :key="cat.id"
        class="categoria"
        :class="{ 'categoria--primera': ci === 0 }"
      >
        <div class="categoria-titulo">{{ cat.nombre }}</div>

        <div
          v-for="(perm, pi) in cat.permisos"
          :key="perm.id"
          class="permiso"
          :class="{ 'permiso--ultimo': pi === cat.permisos.length - 1 }"
        >
          <div class="permiso-texto">
            <div class="permiso-nombre">{{ perm.nombre }}</div>
            <div class="permiso-desc" v-if="perm.descripcion">{{ perm.descripcion }}</div>
          </div>

          <button
            class="toggle"
            :class="{ 'toggle--on': perm.activo }"
            role="switch"
            :aria-checked="perm.activo"
            :aria-label="perm.nombre"
            @click="alternar(perm)"
          >
            <span class="toggle-bola"></span>
          </button>
        </div>
      </div>
    </div>

    <p v-else-if="!cargando" class="vacio">
      No se encontraron permisos{{ busqueda ? ` para "${busqueda}"` : '' }}.
    </p>

    <transition name="slide-up">
      <div class="barra-guardar" v-if="hayCambios">
        <span class="barra-texto">Tienes cambios sin guardar</span>
        <div class="barra-acciones">
          <button class="btn" @click="restablecer" :disabled="guardando">
            Restablecer
          </button>
          <button class="btn" @click="guardar" :disabled="guardando">
            {{ guardando ? 'Guardando...' : 'Guardar cambios' }}
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script lang="ts" setup>
/**
 * Matriz de permisos de un usuario.
 *
 * Sirve para OCULTAR acciones que producirían 403; la autorización real la aplica
 * el backend.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { ref, computed, watch } from 'vue'
import LoadingOverlay from '@/components/LoadingOverlay.vue'
import MensajeToast from '@/components/MensajeToast.vue'
import { obtenerMatrizPermisos, actualizarRol } from '@/services/RegistroUsuariosService'
import { primerError } from '@/services/http'

const props = defineProps<{
  idRol: number
  rolLabel?: string
}>()

const emit = defineEmits<{
  guardado: []
  cerrar: []
}>()

interface Permiso {
  id: number
  name: string
  nombre: string
  descripcion?: string
  activo: boolean
}
interface Categoria {
  id: number
  nombre: string
  permisos: Permiso[]
}

const toast = ref<InstanceType<typeof MensajeToast>>()
const cargando = ref(false)
const guardando = ref(false)
const busqueda = ref('')
const categorias = ref<Categoria[]>([])
const original = ref<Record<number, boolean>>({})

async function cargar() {
  if (!props.idRol) return
  cargando.value = true
  busqueda.value = ''
  try {
    const matriz = await obtenerMatrizPermisos()
    if (!matriz) return

    const rolData = matriz.roles.find(r => r.id === props.idRol)
    const permisosActivos = new Set<string>(rolData?.permisos ?? [])

    categorias.value = matriz.modulos.map(m => ({
      id: m.id,
      nombre: m.nombre,
      permisos: m.permisos.map(p => ({
        id: p.id,
        name: p.name,
        nombre: p.label,
        descripcion: p.descripcion ?? undefined,
        activo: permisosActivos.has(p.name)
      }))
    })).filter(c => c.permisos.length > 0)

    snapshot()
  } finally {
    cargando.value = false
  }
}

function snapshot() {
  const snap: Record<number, boolean> = {}
  categorias.value.forEach(c => c.permisos.forEach(p => { snap[p.id] = p.activo }))
  original.value = snap
}

const categoriasFiltradas = computed<Categoria[]>(() => {
  const q = busqueda.value.trim().toLowerCase()
  if (!q) return categorias.value
  return categorias.value
    .map(cat => ({
      ...cat,
      permisos: cat.permisos.filter(p =>
        p.nombre.toLowerCase().includes(q) ||
        (p.descripcion ?? '').toLowerCase().includes(q)
      )
    }))
    .filter(cat => cat.permisos.length > 0)
})

const hayCambios = computed(() =>
  categorias.value.some(c => c.permisos.some(p => original.value[p.id] !== p.activo))
)

function alternar(perm: Permiso) { perm.activo = !perm.activo }
function borrarTodos() {
  categorias.value.forEach(c => c.permisos.forEach(p => { p.activo = false }))
}
function restablecer() {
  categorias.value.forEach(c => c.permisos.forEach(p => { p.activo = original.value[p.id] ?? false }))
}

async function guardar() {
  guardando.value = true
  try {
    const permisosActivos = categorias.value
      .flatMap(c => c.permisos)
      .filter(p => p.activo)
      .map(p => p.name)

    const result = await actualizarRol(props.idRol, { permisos: permisosActivos })
    if (!result.ok) {
      toast.value?.showToast(primerError(result, 'Error al guardar los permisos.'), 'error')
      return
    }
    snapshot()
    emit('guardado')
  } finally {
    guardando.value = false
  }
}

watch(() => props.idRol, cargar, { immediate: true })
</script>

<style scoped>
.permisos-contenido {
  padding: 1.5rem 1.75rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.permisos-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.permisos-titulo h2 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #111;
}
.permisos-sub {
  margin: 4px 0 0;
  font-size: 0.85rem;
  color: #6b7280;
}
.permisos-sub strong { color: #111; font-weight: 600; }

.permisos-acciones {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.btn-cerrar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid #dde1e6;
  background: white;
  color: #555;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  flex-shrink: 0;
}
.btn-cerrar:hover {
  background: rgb(212, 26, 26);
  color: white;
  border-color: rgb(212, 26, 26);
}

.buscador { margin-bottom: 1.25rem; }
.buscador input {
  width: 100%;
  box-sizing: border-box;
  padding: 0.7rem 0.9rem;
  border: 1px solid #dde1e6;
  border-radius: 9px;
  font-size: 0.9rem;
  background: #f9f9f9;
  color: #111;
  font-family: var(--font-family);
  transition: border-color 0.15s, box-shadow 0.15s;
}
.buscador input::placeholder { color: #9ca3af; }
.buscador input:focus {
  outline: none;
  border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.12);
}

.categoria { margin-top: 1.25rem; padding-top: 0.75rem; border-top: 1px solid #eef0f2; }
.categoria--primera { margin-top: 0; padding-top: 0; border-top: none; }
.categoria-titulo {
  font-size: 0.7rem;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 0.25rem;
}

.permiso {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.5rem;
  padding: 0.85rem 0;
  border-bottom: 1px solid #f0f2f4;
}
.permiso--ultimo { border-bottom: none; }
.permiso-nombre { font-size: 0.92rem; font-weight: 600; color: #111; }
.permiso-desc { font-size: 0.8rem; color: #6b7280; margin-top: 3px; line-height: 1.4; }

.toggle {
  width: 44px;
  height: 24px;
  border-radius: 20px;
  background: #d0d5db;
  border: none;
  position: relative;
  flex-shrink: 0;
  margin-top: 2px;
  cursor: pointer;
  padding: 0;
  transition: background-color 0.18s;
}
.toggle--on { background: #0e69a0; }
.toggle-bola {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.18s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}
.toggle--on .toggle-bola { transform: translateX(20px); }

.btn {
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  height: 40px;
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  transition: all 0.15s ease;
  background-color: #eea222;
  color: black;
}
.btn:disabled { opacity: 0.5; cursor: not-allowed; pointer-events: none; }
.btn:hover:not(:disabled) { background-color: #0e69a0; color: white; }

.barra-guardar {
  position: sticky;
  bottom: 0;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  background: #ffffff;
  border-top: 1px solid #eef0f2;
  padding: 0.85rem 0;
  z-index: 1;
}
.barra-texto { color: #111; font-size: 0.88rem; font-weight: 500; }
.barra-acciones { display: flex; align-items: center; gap: 0.75rem; }

.vacio { color: #999; font-style: italic; margin-top: 1rem; }

.slide-up-enter-active, .slide-up-leave-active { transition: opacity 0.2s, transform 0.2s; }
.slide-up-enter-from, .slide-up-leave-to { opacity: 0; transform: translateY(8px); }
</style>
